

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdmins } from "../redux/actions/AdminActions";
import EditAdminModal from "../pages/admin/EditAdminModal";
import theme from "../components/shared/theme";
import { thStyle, tdStyle } from "../components/shared/themeStyles";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const ITEMS_PER_PAGE = 10;

const dropdownStyle = {
  padding: theme.spacing.sm,
  border: "1px solid #ccc",
  borderRadius: theme.radius.md,
};

// CSV helper
const convertToCSV = (data) => {
  const headers = ["Username", "Email", "Phone", "District", "Municipality", "Created At"];
  const rows = data.map((a) => [
    a.username,
    a.email,
    a.phone,
    a.district,
    a.municipality,
    new Date(a.created_at).toLocaleDateString("en-IN"),
  ]);
  return [headers, ...rows].map((row) => row.join(",")).join("\n");
};

const ViewAdmin = () => {
  const dispatch = useDispatch();
  const { admins = [], loading, error } = useSelector((state) => state.admin || {});
  const { token } = useSelector((state) => state.auth) || {};

  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchAdmins(token));
  }, [dispatch, token]);

  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setSelectedAdmin(null);
    setShowEditModal(false);
  };

  const resetDates = () => {
    setFromDate("");
    setToDate("");
  };

  const handleDownloadCSV = () => {
    const blob = new Blob([convertToCSV(filteredAdmins)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "admin_list.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const districts = [...new Set(admins.map((a) => a.district).filter(Boolean))];
  const municipalities = [...new Set(admins.map((a) => a.municipality).filter(Boolean))];

  const filteredAdmins = admins.filter((a) => {
    const matchDistrict = selectedDistrict ? a.district === selectedDistrict : true;
    const matchMunicipality = selectedMunicipality ? a.municipality === selectedMunicipality : true;
    const matchSearch = searchTerm
      ? [a.username, a.email, a.phone, a.district, a.municipality]
          .join(" ")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      : true;
    const createdAt = new Date(a.created_at);
    const matchFromDate = fromDate ? createdAt >= new Date(fromDate) : true;
    const matchToDate = toDate ? createdAt <= new Date(toDate) : true;
    return matchDistrict && matchMunicipality && matchSearch && matchFromDate && matchToDate;
  });

  const totalPages = Math.ceil(filteredAdmins.length / ITEMS_PER_PAGE);
  const paginatedAdmins = filteredAdmins.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div style={{ backgroundColor: theme.colors.background, padding: theme.spacing.md, minHeight: "100vh" }}>
      <h1 style={{ fontSize: theme.font.size["2xl"], fontWeight: theme.font.weight.bold, marginBottom: theme.spacing.md }}>
        ULB Admin List
      </h1>

      {/* Filters */}
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing.sm,
        marginBottom: theme.spacing.lg,
        backgroundColor: "#fff",
        padding: theme.spacing.md,
        borderRadius: theme.radius.xl,
        boxShadow: theme.shadow.soft
      }}>
        <select value={selectedDistrict} onChange={(e) => { setSelectedDistrict(e.target.value); setCurrentPage(1); }} style={dropdownStyle}>
          <option value="">All Districts</option>
          {districts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={selectedMunicipality} onChange={(e) => { setSelectedMunicipality(e.target.value); setCurrentPage(1); }} style={dropdownStyle}>
          <option value="">All Municipalities</option>
          {municipalities.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} style={{ padding: theme.spacing.sm, border: "1px solid #ccc", borderRadius: theme.radius.md, minWidth: "250px" }} />
        <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={dropdownStyle} />
        <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={dropdownStyle} />
        <button onClick={resetDates} style={{ backgroundColor: theme.colors.primary, color: "#fff", padding: theme.spacing.sm, borderRadius: theme.radius.md }}>Reset Date</button>
        <button onClick={handleDownloadCSV} style={{ backgroundColor: theme.colors.secondary, color: "#fff", padding: theme.spacing.sm, borderRadius: theme.radius.md }}>Export CSV</button>
      </div>

      {/* Table */}
      {loading ? (
        <p style={{ fontSize: theme.font.size.base }}>Loading admins...</p>
      ) : error ? (
        <div style={{ backgroundColor: theme.colors.dangerLight, border: `1px solid ${theme.colors.danger}`, color: theme.colors.dangerText, padding: theme.spacing.sm, borderRadius: theme.radius.md }}>
          Error: {error}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="min-w-[800px] w-full text-sm text-left">
              <thead style={{ backgroundColor: theme.colors.grayDark, color: "white" }}>
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Username</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Phone</th>
                  <th style={thStyle}>District</th>
                  <th style={thStyle}>Municipality</th>
                  <th style={thStyle}>Programme</th>
                  <th style={thStyle}>Created At</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedAdmins.map((admin, index) => (
                  <tr key={admin.id || index} className="hover:bg-gray-50">
                    <td style={tdStyle}>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                    <td style={tdStyle}>{admin.username}</td>
                    <td style={tdStyle}>{admin.email}</td>
                    <td style={tdStyle}>{admin.phone}</td>
                    <td style={tdStyle}>{admin.district || "-"}</td>
                    <td style={tdStyle}>{admin.municipality || "-"}</td>
                    <td style={tdStyle}>{admin.programme || "-"}</td>
                    <td style={tdStyle}>{new Date(admin.created_at).toLocaleDateString("en-IN")}</td>
                    <td style={tdStyle}>
                      <button className="px-2 py-1 text-sm bg-blue-600 text-white rounded" onClick={() => openEditModal(admin)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: theme.spacing.md }}>
            <button
              onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              style={{ display: "flex", alignItems: "center", gap: 4, padding: "8px 12px", backgroundColor: "#ccc", borderRadius: theme.radius.md, opacity: currentPage === 1 ? 0.5 : 1 }}
            >
              <FiChevronLeft /> Previous
            </button>
            <span style={{ fontSize: theme.font.size.sm }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{ display: "flex", alignItems: "center", gap: 4, padding: "8px 12px", backgroundColor: "#ccc", borderRadius: theme.radius.md, opacity: currentPage === totalPages ? 0.5 : 1 }}
            >
              Next <FiChevronRight />
            </button>
          </div>
        </>
      )}

      {showEditModal && selectedAdmin && (
        <EditAdminModal isOpen={showEditModal} admin={selectedAdmin} onClose={closeEditModal} />
      )}
    </div>
  );
};

export default ViewAdmin;
